import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useCurrentBusiness } from "@/hooks/use-current-business";

interface AnomalyResult {
  success?: boolean;
  message?: string;
  isAnomaly: boolean;
  confidence: number;
  explanation: string;
  adjusted?: boolean;
  adjustedReason?: string;
  flags?: string[];
  factors?: string[];
  recommendations?: string[];
}

interface InsightResult {
  success: boolean;
  timeframe: string;
  summary: {
    totalIncome: string;
    totalExpenses: string;
    netProfit: string;
    profitMargin: string;
    transactionCount: number;
  };
  insights: Array<{
    type: string;
    title: string;
    description: string;
    severity: 'positive' | 'warning' | 'info';
  }>;
  recommendations: string[];
}

export function useTransactionAnomaly(transactionId: number) {
  const [anomalyData, setAnomalyData] = useState<AnomalyResult | null>(null);
  const { toast } = useToast();
  const { currentBusiness } = useCurrentBusiness();
  const businessId = currentBusiness?.id;

  const { mutateAsync: detectAnomaly, isPending: isLoading, error } = useMutation({
    mutationFn: async () => {
      if (!businessId) {
        throw new Error("No business selected");
      }
      
      const response = await apiRequest(
        "POST", 
        `/api/businesses/${businessId}/transactions/${transactionId}/detect-anomaly`
      );
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to analyze transaction");
      }
      
      return await response.json();
    },
    onSuccess: (data) => {
      setAnomalyData(data);
    },
    onError: (error: Error) => {
      toast({
        title: "Anomaly detection failed",
        description: error.message,
        variant: "destructive",
      });
    },
  });

  return {
    detectAnomaly,
    anomalyData,
    isLoading,
    error,
  };
}

export function useFinancialInsights(timeframe: string = 'month') {
  const { currentBusiness } = useCurrentBusiness();
  const businessId = currentBusiness?.id;
  const { toast } = useToast();

  const {
    data: insights,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["financial-insights", businessId, timeframe],
    queryFn: async (): Promise<InsightResult> => {
      if (!businessId) {
        return { success: false, insights: { insights: [], recommendations: [], summary: {} } } as any;
      }
      try {
        const response = await fetch(
          `/api/businesses/${businessId}/insights?timeframe=${timeframe}`,
          { credentials: "include" }
        );
        return await response.json();
      } catch {
        return { success: false, insights: { insights: [], recommendations: [], summary: {} } } as any;
      }
    },
    enabled: !!businessId,
    retry: false,
  });

  return {
    insights,
    isLoading,
    error,
    refetch,
  };
}